/**
 * EPSS Cell Renderer
 *
 * Renders the EPSS (Exploit Prediction Scoring System) score.
 * Displays probability percentage of exploitation occurring in the next 30 days.
 */

import React from 'react';

import type { RiskWithVulnerability } from '@/hooks/useRisks';

interface EPSSCellProps {
  risk: RiskWithVulnerability;
  /** The EPSS value to display */
  value?: string | number;
}

/**
 * Memoized cell renderer for EPSS score column
 *
 * Displays the EPSS score as plain text with default styling.
 * The table component handles copy-to-clipboard functionality.
 *
 * @param risk - The vulnerability/risk
 * @param value - The EPSS score value (provided by table component)
 * @returns Formatted EPSS score or dash if unavailable
 */
export const EPSSCell: React.FC<EPSSCellProps> = React.memo(
  ({ risk, value }) => {
    const epssValue = value ?? risk.epss;

    if (epssValue === undefined || epssValue === null) {
      return <>-</>;
    }

    return <span className="text-chariot-text-primary">{epssValue}</span>;
  }
);

EPSSCell.displayName = 'EPSSCell';
